import type {
  WashActivationStatus,
  WashAppointmentStatus,
  WashCreditStatus,
  WashCycleStatus,
  WashDayClosing,
  WashFaults,
  WashMeterOption,
  WashOccupancy,
  WashProgram,
  WashProgramKind,
  WashReadySmsStatus,
  WashStartMode,
  WashSummary,
  WashSummaryRow,
  WashUnallocatedUtility,
  WashUnitCurrentCycle,
  WashUnitState,
  WashUnitView,
  WashWorkOrderStatus,
  WashWorkOrderSummary,
} from "@station/contracts";
import {
  Decimal,
  istanbulDateTimeToUtc,
  type LocalDate,
  localDate,
  Money,
  toIstanbulDate,
} from "@station/core";
import type { Tone } from "@station/ui";
import {
  type ChartOption,
  formatChartNumber,
  GRID,
  LEGEND_POSITION,
  VALUE_AXIS_LABEL,
} from "../fuel/chart-options.js";
import { apiDecimalToTrText, trTextToApiDecimal } from "../pos/pos-values.js";

/**
 * Yıkama ekranlarının SAF yardımcıları (Prompt 18 web; birim testli): durum renkleri, süre / bakiye biçimleri, çalışma
 * saati düzenleyicisinin normalleştirmesi, ünite ayarı / program / reçete / randevu / iş emri formlarının istek gövdesine
 * dönüşümü, iş emri panosu ve sıra ekranı gruplaması, rapor grafik seçenekleri (doluluk ısı haritası dâhil).
 *
 * Para `Money`, miktar `Decimal` ile hesaplanır; grafiklerde yalnızca ÇİZİM anında sayıya çevrilir (AGENTS.md §3.2).
 */

// ── Form doğrulaması (sözleşme şeması + Türkçe ileti) ───────────────────────────
interface ZodIssueLike {
  code: string;
  path: readonly PropertyKey[];
  message: string;
  minimum?: unknown;
  maximum?: unknown;
  origin?: unknown;
}

/**
 * Zod'un İngilizce varsayılan iletisini Türkçeye çevirir; sözleşmenin kendi (Türkçe) iletisi olduğu gibi kalır. Formlar
 * sözleşme şemasını AYNEN kullanır (istemci ile sunucu aynı kuralı uygular); yalnızca varsayılan iletiler çevrilir.
 */
export function issueText(issue: ZodIssueLike): string {
  if (!/^(Invalid|Too (small|big)|Expected|Required)/.test(issue.message)) return issue.message;
  const isString = issue.origin === "string" || issue.origin === "array";
  if (issue.code === "too_small")
    return isString
      ? Number(issue.minimum) <= 1
        ? "Bu alan zorunludur"
        : `En az ${String(issue.minimum)} karakter olmalıdır`
      : `En az ${String(issue.minimum)} olmalıdır`;
  if (issue.code === "too_big")
    return isString
      ? `En çok ${String(issue.maximum)} karakter olabilir`
      : `En çok ${String(issue.maximum)} olabilir`;
  if (issue.code === "invalid_format") return "Seçim yapın";
  if (issue.code === "invalid_value") return "Geçerli bir seçenek seçin";
  if (issue.code === "invalid_type") return "Geçerli bir değer girin";
  return "Geçersiz değer";
}

/**
 * Şema + gövde → alan yolu (`openingHours.0.to`) başına İLK ileti. Başarılıysa boş nesne. `safeParse` kullanılır; şema
 * dışı istisna fırlatmaz.
 */
export function formIssues(
  schema: {
    safeParse: (value: unknown) => { success: boolean; error?: { issues: readonly ZodIssueLike[] } };
  },
  body: unknown,
): Record<string, string> {
  const result = schema.safeParse(body);
  if (result.success || !result.error) return {};
  const issues: Record<string, string> = {};
  for (const issue of result.error.issues) {
    const key = issue.path.map(String).join(".");
    if (!(key in issues)) issues[key] = issueText(issue);
  }
  return issues;
}

// ── Durum renkleri (rozet METİN yazar; anlam yalnızca renkle taşınmaz) ─────────
export const UNIT_STATE_TONE: Record<WashUnitState, Tone> = {
  IDLE: "success",
  RUNNING: "info",
  FAULT: "danger",
  OFFLINE: "neutral",
};

export const ACTIVATION_STATUS_TONE: Record<WashActivationStatus, Tone> = {
  PENDING: "warning",
  STARTED: "info",
  COMPLETED: "success",
  FAILED: "danger",
  EXPIRED: "neutral",
  CANCELLED: "neutral",
};

export const CYCLE_STATUS_TONE: Record<WashCycleStatus, Tone> = {
  COMPLETED: "success",
  ABORTED: "warning",
  FAULT: "danger",
};

export const CREDIT_STATUS_TONE: Record<WashCreditStatus, Tone> = {
  ACTIVE: "success",
  EXPIRED: "neutral",
  BLOCKED: "danger",
};

export const APPOINTMENT_STATUS_TONE: Record<WashAppointmentStatus, Tone> = {
  BOOKED: "info",
  CHECKED_IN: "success",
  CANCELLED: "neutral",
  NO_SHOW: "warning",
};

export const WORK_ORDER_STATUS_TONE: Record<WashWorkOrderStatus, Tone> = {
  WAITING: "warning",
  IN_PROGRESS: "info",
  READY: "success",
  DELIVERED: "neutral",
  CANCELLED: "danger",
};

export const READY_SMS_TONE: Record<WashReadySmsStatus, Tone> = {
  NOT_SENT: "neutral",
  SENT: "success",
  FAILED: "danger",
  NO_PHONE: "warning",
};

export const DAY_CLOSING_TONE: Record<WashDayClosing["status"], Tone> = {
  POSTED: "success",
  QUEUED: "warning",
  EMPTY: "neutral",
};

/** Aktivasyon sonuçlandı mı (canlı durum izlemesi durur). */
export function isActivationFinal(status: WashActivationStatus): boolean {
  return status !== "PENDING";
}

// ── Süre, bakiye, saat ────────────────────────────────────────────────────────
/**
 * Saniye → okunur süre: `1 sa 5 dk`, `12 dk 30 sn`, `45 sn`. Saat varsa saniye yazılmaz (ekranda gürültü). Eksi değer
 * (kredi bakiyesi eksiye düşebilir) `−` işaretiyle yazılır. Tam sayı olmayan girdi aşağı yuvarlanır.
 */
export function formatDuration(seconds: number | string | null | undefined): string {
  if (seconds === null || seconds === undefined || seconds === "") return "—";
  const value = typeof seconds === "string" ? Number.parseInt(seconds, 10) : Math.trunc(seconds);
  if (!Number.isFinite(value)) return "—";
  const sign = value < 0 ? "−" : "";
  const abs = Math.abs(value);
  const hours = Math.floor(abs / 3600);
  const minutes = Math.floor((abs % 3600) / 60);
  const rest = abs % 60;
  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours} sa`);
  if (minutes > 0) parts.push(`${minutes} dk`);
  if (hours === 0 && (rest > 0 || parts.length === 0)) parts.push(`${rest} sn`);
  return `${sign}${parts.join(" ")}`;
}

/** Program süresi (sn) → düzenleme alanındaki dakika metni ("7,5"). */
export function secondsToMinutesText(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return "";
  return apiDecimalToTrText(Decimal.from(seconds).div(60).round(2).toString());
}

/**
 * Dakika metni ("7,5") → saniye. Boş → null; sayı değilse `NaN` (Zod "sayı olmalı" hatası alanın altında görünür —
 * sessizce null'a düşmez). Saniye tam sayıya yuvarlanır.
 */
export function minutesTextToSeconds(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed === "") return null;
  const decimal = trTextToApiDecimal(trimmed);
  if (decimal === "" || !/^-?\d+(\.\d+)?$/.test(decimal)) return Number.NaN;
  return Number(Decimal.from(decimal).mul(60).round(0).toString());
}

const CLOCK = new Intl.DateTimeFormat("tr-TR", {
  timeZone: "Europe/Istanbul",
  hour: "2-digit",
  minute: "2-digit",
});

/** Zaman damgası → İstanbul saatiyle `14:05`. */
export function formatClock(value: string | null | undefined): string {
  return value ? CLOCK.format(new Date(value)) : "—";
}

// ── Ünite panosu: sürmekte olan çalışma ────────────────────────────────────────
export interface CurrentCycleView {
  /** Program bilinmiyorsa (makine tahsilatı, kredi, eşleşmeyen çalışma) null → "Çalışıyor". */
  program: string | null;
  startedAt: string;
  /** Başlangıçtan bu yana (sn, ≥ 0). */
  elapsedSeconds: number;
  /** Beklenen bitişe kalan (sn, ≥ 0); bitiş bilinmiyorsa null. */
  remainingSeconds: number | null;
  /** Beklenen bitiş geçti (makine henüz bitti demedi). */
  overdue: boolean;
}

/**
 * Sunucunun `live.currentCycle`'ı (açık RUNNING aralığı + eşleşen başlatma) → pano metni. Kalan süre beklenen bitişten,
 * geçen süre başlangıçtan hesaplanır; saat kayması eksi süre üretmesin diye 0'da kırpılır.
 */
export function currentCycleView(cycle: WashUnitCurrentCycle | null, now: number): CurrentCycleView | null {
  if (!cycle) return null;
  const started = new Date(cycle.startedAt).getTime();
  const expected = cycle.expectedEndAt ? new Date(cycle.expectedEndAt).getTime() : null;
  const program =
    cycle.programName === null
      ? null
      : cycle.programCode
        ? `${cycle.programCode} ${cycle.programName}`
        : cycle.programName;
  return {
    program,
    startedAt: cycle.startedAt,
    elapsedSeconds: Math.max(0, Math.floor((now - started) / 1000)),
    remainingSeconds: expected === null ? null : Math.max(0, Math.round((expected - now) / 1000)),
    overdue: expected !== null && now > expected,
  };
}

// ── Plaka ──────────────────────────────────────────────────────────────────────
const PLATE_GROUPS = /^(\d{2})([A-Z]{1,3})(\d{2,5})$/;

/**
 * Plaka görünümü. Açık plaka `34 ABC 123` biçiminde gruplanır; maskeli plaka (`34****23`, sunucu maskeler) OLDUĞU GİBİ
 * gösterilir — istemci maskeyi açmaz, yeniden biçimlendirip anlam değiştirmez.
 */
export function displayPlate(plate: string | null | undefined, masked = false): string {
  if (!plate) return "—";
  if (masked || plate.includes("*")) return plate;
  const match = PLATE_GROUPS.exec(plate.replace(/\s+/g, "").toUpperCase());
  return match ? `${match[1]} ${match[2]} ${match[3]}` : plate;
}

/**
 * Sıra ekranı plakası: sunucunun maskelediği plaka ekranın ekran okuyucu metninde yıldız okunmasın diye iki parçaya
 * ayrılır (görünen + erişilebilir ad). Maske yoksa (beklenmez) yine de ilk 2 ve son 2 karakter dışı gizlenir.
 */
export function queuePlate(maskedPlate: string | null | undefined): { visible: string; spoken: string } {
  if (!maskedPlate) return { visible: "—", spoken: "Plaka yok" };
  const masked = maskedPlate.includes("*")
    ? maskedPlate
    : maskedPlate.length > 4
      ? `${maskedPlate.slice(0, 2)}${"*".repeat(maskedPlate.length - 4)}${maskedPlate.slice(-2)}`
      : maskedPlate;
  const start = masked.match(/^[^*]*/)?.[0] ?? "";
  const end = masked.match(/[^*]*$/)?.[0] ?? "";
  return { visible: masked, spoken: `Plaka ${start} ile başlayan, ${end} ile biten` };
}

// ── Çalışma saatleri düzenleyicisi ─────────────────────────────────────────────
export interface OpeningWindowRow {
  key: string;
  /** ISO hafta günleri (1 = Pazartesi … 7 = Pazar); boş = her gün. */
  weekdays: number[];
  from: string;
  to: string;
}

export const ISO_WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;
const TIME = /^([01]\d|2[0-3]):([0-5]\d)$/;

let rowCounter = 0;
export function newOpeningRow(partial: Partial<Omit<OpeningWindowRow, "key">> = {}): OpeningWindowRow {
  rowCounter += 1;
  return { key: `row-${rowCounter}`, weekdays: [], from: "09:00", to: "18:00", ...partial };
}

export function openingRowsFrom(
  windows: readonly { weekdays: readonly number[]; from: string; to: string }[],
): OpeningWindowRow[] {
  // "Her gün" (boş liste) düzenleyicide yedi gün seçili görünür: kullanıcı bir günü kaldırarak daraltır.
  return windows.map((window) =>
    newOpeningRow({
      weekdays: window.weekdays.length === 0 ? [...ISO_WEEKDAYS] : [...window.weekdays],
      from: window.from,
      to: window.to,
    }),
  );
}

/**
 * Düzenleyici satırları → API gövdesi: gün listesi sıralanır ve tekilleşir, saatler kırpılır, iki saati de boş satır
 * atılır (kullanıcı eklediği boş satırı silmeyi unutmuş olabilir). Yedi günün hepsi seçiliyse "her gün" (boş dizi) yazılır.
 */
export function openingHoursBody(
  rows: readonly OpeningWindowRow[],
): Array<{ weekdays: number[]; from: string; to: string }> {
  return rows
    .map((row) => ({ ...row, from: row.from.trim(), to: row.to.trim() }))
    .filter((row) => row.from !== "" || row.to !== "")
    .map((row) => {
      const days = [...new Set(row.weekdays)].filter((day) => day >= 1 && day <= 7).sort((a, b) => a - b);
      return { weekdays: days.length === 7 ? [] : days, from: row.from, to: row.to };
    });
}

export type OpeningRowIssue = "time" | "order" | "overlap";

/** Satır bazında sorun: geçersiz saat, bitiş ≤ başlangıç, aynı günde örtüşen pencere (sunucu da reddeder). */
export function openingHoursIssues(rows: readonly OpeningWindowRow[]): Record<string, OpeningRowIssue> {
  const issues: Record<string, OpeningRowIssue> = {};
  const body = rows.map((row) => ({ key: row.key, from: row.from.trim(), to: row.to.trim(), row }));
  for (const item of body) {
    if (item.from === "" && item.to === "") continue;
    if (!TIME.test(item.from) || !TIME.test(item.to)) issues[item.key] = "time";
    else if (item.from >= item.to) issues[item.key] = "order";
  }
  const days = (row: OpeningWindowRow) => (row.weekdays.length === 0 ? [...ISO_WEEKDAYS] : row.weekdays);
  for (let i = 0; i < body.length; i += 1)
    for (let j = i + 1; j < body.length; j += 1) {
      const a = body[i]!;
      const b = body[j]!;
      if (issues[a.key] || issues[b.key] || a.from === "" || b.from === "") continue;
      const shared = days(a.row).some((day) => days(b.row).includes(day));
      if (shared && a.from < b.to && b.from < a.to) issues[b.key] = "overlap";
    }
  return issues;
}

/**
 * Pencerenin okunur özeti: ardışık günler aralıkla yazılır ("Pzt–Cum 08:00–20:00"), boş gün listesi "Her gün".
 * `labels` ISO gün sırasıyla (1 → Pazartesi) 7 kısa ad içerir.
 */
export function describeOpeningWindow(
  window: { weekdays: readonly number[]; from: string; to: string },
  labels: readonly string[],
  everyDay: string,
): string {
  const days = [...new Set(window.weekdays)].sort((a, b) => a - b);
  if (days.length === 0 || days.length === 7) return `${everyDay} ${window.from}–${window.to}`;
  const groups: number[][] = [];
  for (const day of days) {
    const last = groups.at(-1);
    if (last && last.at(-1) === day - 1) last.push(day);
    else groups.push([day]);
  }
  const text = groups
    .map((group) =>
      group.length >= 3
        ? `${labels[group[0]! - 1]}–${labels[group.at(-1)! - 1]}`
        : group.map((day) => labels[day - 1]).join(", "),
    )
    .join(", ");
  return `${text} ${window.from}–${window.to}`;
}

// ── Ünite ayarı ────────────────────────────────────────────────────────────────
type WashUnitType = WashUnitView["unitType"];

/** Ünite türüne uyan program türü (sunucu kuralıyla aynı: AUTOMATIC ↔ TUNNEL / ROLLOVER …). */
export function programKindForUnit(unitType: WashUnitType): WashProgramKind {
  if (unitType === "SELF_SERVICE_BAY") return "SELF_SERVICE";
  if (unitType === "HAND_WASH") return "HAND_WASH";
  return "AUTOMATIC";
}

/** El yıkama yalnızca elle; makine üniteleri üç biçimden birini seçer. */
export function allowedStartModes(unitType: WashUnitType): WashStartMode[] {
  return unitType === "HAND_WASH" ? ["MANUAL"] : ["REMOTE", "CODE", "MANUAL"];
}

export interface UnitSettingsFormValues {
  deviceRef: string;
  startMode: WashStartMode;
  capacity: string;
  slotMinutes: string;
  openingHours: OpeningWindowRow[];
  waterMeterId: string;
  electricityMeterId: string;
  programs: Array<{ programId: string; deviceProgramCode: string }>;
}

export function unitSettingsFormFrom(unit: WashUnitView): UnitSettingsFormValues {
  const modes = allowedStartModes(unit.unitType);
  return {
    deviceRef: unit.settings.deviceRef ?? "",
    startMode: modes.includes(unit.settings.startMode) ? unit.settings.startMode : modes[0]!,
    capacity: String(unit.settings.capacity),
    slotMinutes: String(unit.settings.slotMinutes),
    openingHours: openingRowsFrom(unit.settings.openingHours),
    waterMeterId: unit.settings.waterMeterId ?? "",
    electricityMeterId: unit.settings.electricityMeterId ?? "",
    programs: unit.programs.map((program) => ({
      programId: program.programId,
      deviceProgramCode: program.deviceProgramCode ?? "",
    })),
  };
}

const toInt = (text: string): number | string => {
  const trimmed = text.trim();
  return /^\d+$/.test(trimmed) ? Number(trimmed) : trimmed;
};

/**
 * Ünite ayarı formu → `PUT /wash/units/:id/settings` gövdesi (doğrulamadan ÖNCE): boş cihaz başvurusu null; MANUAL
 * ünitede cihaz başvurusu gönderilmez (ekranda gizli alan hata üretmesin); makine ünitesinde kapasite 1 (sunucu kuralı —
 * alan ekranda yoktur); boş sayaç seçimi null; tam sayı alanları sayıya çevrilir, çevrilemeyen metin olduğu gibi kalır ki
 * Zod hatası alanın altında görünsün.
 */
export function unitSettingsBody(
  values: UnitSettingsFormValues,
  unitType: WashUnitType,
  version: number,
): Record<string, unknown> {
  const deviceRef = values.deviceRef.trim();
  return {
    deviceRef: values.startMode === "MANUAL" || deviceRef === "" ? null : deviceRef,
    startMode: values.startMode,
    capacity: unitType === "HAND_WASH" ? toInt(values.capacity) : 1,
    slotMinutes: toInt(values.slotMinutes),
    openingHours: openingHoursBody(values.openingHours),
    waterMeterId: values.waterMeterId === "" ? null : values.waterMeterId,
    electricityMeterId: values.electricityMeterId === "" ? null : values.electricityMeterId,
    version,
  };
}

/** Ünitede sunulan programlar → `PUT /wash/units/:id/programs` gövdesi (boş makine kodu null; el yıkamada hep null). */
export function unitProgramsBody(
  values: Pick<UnitSettingsFormValues, "programs">,
  unitType: WashUnitType,
): { programs: Array<{ programId: string; deviceProgramCode: string | null }> } {
  return {
    programs: values.programs.map((program) => {
      const code = program.deviceProgramCode.trim();
      return {
        programId: program.programId,
        deviceProgramCode: unitType === "HAND_WASH" || code === "" ? null : code,
      };
    }),
  };
}

/** Program listesi değişti mi (değişmediyse ayrı PUT atılmaz; sıra önemsizdir). */
export function unitProgramsChanged(
  unit: WashUnitView,
  values: Pick<UnitSettingsFormValues, "programs">,
): boolean {
  const before = new Map(
    unit.programs.map((program) => [program.programId, program.deviceProgramCode ?? ""]),
  );
  if (before.size !== values.programs.length) return true;
  return values.programs.some(
    (program) =>
      !before.has(program.programId) || before.get(program.programId) !== program.deviceProgramCode.trim(),
  );
}

/**
 * Ünite ayarındaki sayaç seçenekleri (`GET /wash/meters`): türe göre süzülür; seçili sayaç listede yoksa (pasife alınmış)
 * adıyla EN BAŞA eklenir ki seçim sessizce kaybolmasın. Etiket: ad + üst sayaç ("↳ üst sayaç" metni çağırandan).
 */
export function meterOptions(
  meters: readonly WashMeterOption[],
  meterType: WashMeterOption["meterType"],
  current: { id: string | null; name: string | null },
  parentLabel: (parentName: string) => string,
): Array<{ id: string; label: string }> {
  const options = meters
    .filter((meter) => meter.meterType === meterType)
    .map((meter) => ({
      id: meter.id,
      label: meter.parentName ? `${meter.name} (${parentLabel(meter.parentName)})` : meter.name,
    }));
  if (current.id && !options.some((option) => option.id === current.id))
    options.unshift({ id: current.id, label: current.name ?? current.id });
  return options;
}

// ── Program ve reçete ──────────────────────────────────────────────────────────
export interface RecipeLineValues {
  productId: string;
  /** Türkiye biçimli miktar ("0,25"). */
  quantity: string;
}

export interface ProgramFormValues {
  code: string;
  name: string;
  kind: WashProgramKind;
  productId: string;
  /** Tahmini süre (dakika, Türkiye biçimi); self-serviste yok. */
  durationMinutes: string;
  waterLitres: string;
  electricityKwh: string;
  isActive: boolean;
  recipe: RecipeLineValues[];
}

export function emptyProgramForm(kind: WashProgramKind = "AUTOMATIC"): ProgramFormValues {
  return {
    code: "",
    name: "",
    kind,
    productId: "",
    durationMinutes: kind === "SELF_SERVICE" ? "" : "10",
    waterLitres: "0",
    electricityKwh: "0",
    isActive: true,
    recipe: [],
  };
}

export function programFormFrom(program: WashProgram): ProgramFormValues {
  return {
    code: program.code,
    name: program.name,
    kind: program.kind,
    productId: program.productId,
    durationMinutes: secondsToMinutesText(program.durationSeconds),
    waterLitres: apiDecimalToTrText(program.waterLitres),
    electricityKwh: apiDecimalToTrText(program.electricityKwh),
    isActive: program.isActive,
    recipe: program.consumptions.map((line) => ({
      productId: line.productId,
      quantity: apiDecimalToTrText(line.quantity),
    })),
  };
}

const decimalOrDefault = (text: string, fallback: string): string => {
  const value = trTextToApiDecimal(text);
  return value === "" ? fallback : value;
};

/** Reçete satırları → gövde: ürünü seçilmemiş boş satır atılır; miktar API biçimine (nokta) çevrilir. */
export function recipeBody(
  lines: readonly RecipeLineValues[],
): Array<{ productId: string; quantity: string }> {
  return lines
    .filter((line) => line.productId !== "" || line.quantity.trim() !== "")
    .map((line) => ({ productId: line.productId, quantity: trTextToApiDecimal(line.quantity) }));
}

/**
 * Program formu → `POST /wash/programs` gövdesi (doğrulamadan ÖNCE): süre dakikadan saniyeye; self-serviste süre YOK
 * (null — alan ekranda gizli); boş su / elektrik "0"; reçete satırları normalleştirilir.
 */
export function createProgramBody(values: ProgramFormValues): Record<string, unknown> {
  return {
    code: values.code.trim().toUpperCase(),
    kind: values.kind,
    name: values.name.trim(),
    productId: values.productId,
    durationSeconds: values.kind === "SELF_SERVICE" ? null : minutesTextToSeconds(values.durationMinutes),
    waterLitres: decimalOrDefault(values.waterLitres, "0"),
    electricityKwh: decimalOrDefault(values.electricityKwh, "0"),
    isActive: values.isActive,
    consumptions: recipeBody(values.recipe),
  };
}

/** Geçersiz metin (ör. "?") DEĞİŞMİŞ sayılır: gövdeye girer ve Zod hatası alanın altında görünür. */
const decimalChanged = (next: unknown, previous: string): boolean =>
  typeof next !== "string" || !/^\d+(\.\d+)?$/.test(next) || !Decimal.from(next).eq(previous);

/** Düzenleme → `PATCH /wash/programs/:id` gövdesi: yalnızca DEĞİŞEN alanlar + sürüm (kod ve tür değişmez). */
export function updateProgramBody(values: ProgramFormValues, program: WashProgram): Record<string, unknown> {
  const next = createProgramBody(values);
  const patch: Record<string, unknown> = { version: program.version };
  if (next.name !== program.name) patch.name = next.name;
  if (next.productId !== program.productId) patch.productId = next.productId;
  if (program.kind !== "SELF_SERVICE" && next.durationSeconds !== program.durationSeconds)
    patch.durationSeconds = next.durationSeconds;
  if (decimalChanged(next.waterLitres, program.waterLitres)) patch.waterLitres = next.waterLitres;
  if (decimalChanged(next.electricityKwh, program.electricityKwh)) patch.electricityKwh = next.electricityKwh;
  if (next.isActive !== program.isActive) patch.isActive = next.isActive;
  return patch;
}

/** Reçete değişti mi (değişmediyse `PUT …/consumptions` atılmaz; sıra önemsizdir). */
export function recipeChanged(program: WashProgram, lines: readonly RecipeLineValues[]): boolean {
  const next = recipeBody(lines);
  if (next.length !== program.consumptions.length) return true;
  const before = new Map(program.consumptions.map((line) => [line.productId, line.quantity]));
  return next.some((line) => {
    const previous = before.get(line.productId);
    if (previous === undefined || !/^\d+(\.\d+)?$/.test(line.quantity)) return true;
    return !Decimal.from(previous).eq(line.quantity);
  });
}

/** Program ürünü seçeneği: iş birimi WASH, türü WASH_SERVICE / SERVICE; self-serviste birim DAKİKA (yalnızca orada). */
export function isProgramProductFor(
  product: { businessUnit: string; productType: string; unit: string; isActive: boolean },
  kind: WashProgramKind,
): boolean {
  if (!product.isActive || product.businessUnit !== "WASH") return false;
  if (product.productType !== "WASH_SERVICE" && product.productType !== "SERVICE") return false;
  return kind === "SELF_SERVICE" ? product.unit === "MINUTE" : product.unit !== "MINUTE";
}

// ── Kimyasal kapsama ───────────────────────────────────────────────────────────
export type CoverLevel = "unknown" | "none" | "low" | "ok";

/** Kapsama günü → uyarı düzeyi: tüketim yoksa bilinmez; stok bittiyse / eksideyse "yok"; eşiğin altında "az". */
export function coverLevel(
  item: { daysOfCover: string | null; quantity: string | null },
  thresholdDays = 7,
): CoverLevel {
  if (item.quantity !== null && !Decimal.from(item.quantity).isPositive()) return "none";
  if (item.daysOfCover === null) return "unknown";
  return Decimal.from(item.daysOfCover).lt(thresholdDays) ? "low" : "ok";
}

export const COVER_TONE: Record<CoverLevel, Tone> = {
  unknown: "neutral",
  none: "danger",
  low: "warning",
  ok: "success",
};

// ── Ayarlar: hazır SMS önizlemesi ──────────────────────────────────────────────
export const SMS_PLACEHOLDERS = ["{plaka}", "{istasyon}", "{isEmriNo}"] as const;

/** Şablondaki yer tutucuları örnek değerlerle doldurur (sunucudaki metin üretimiyle aynı yer tutucular). */
export function smsPreview(
  template: string,
  sample: { plate: string; station: string; orderNo: string },
): string {
  return template
    .replaceAll("{plaka}", sample.plate)
    .replaceAll("{istasyon}", sample.station)
    .replaceAll("{isEmriNo}", sample.orderNo);
}

/** Şablonda tanınmayan `{…}` yer tutucuları (yazım hatası uyarısı). */
export function unknownPlaceholders(template: string): string[] {
  const found = template.match(/\{[^{}]*\}/g) ?? [];
  return [...new Set(found.filter((token) => !(SMS_PLACEHOLDERS as readonly string[]).includes(token)))];
}

/** SMS uzunluğu (Türkçe karakterli ileti tek parçada 70 karakterdir; bilgi amaçlı parça sayısı). */
export function smsSegments(text: string): number {
  if (text.length === 0) return 0;
  const unicode = /[^\x20-\x7E\n]/.test(text);
  const single = unicode ? 70 : 160;
  const multi = unicode ? 67 : 153;
  return text.length <= single ? 1 : Math.ceil(text.length / multi);
}

// ── Randevu ────────────────────────────────────────────────────────────────────
export interface AppointmentFormValues {
  programIds: string[];
  startsAt: string;
  plate: string;
  customerProfileId: string | null;
  contactName: string;
  contactPhone: string;
  note: string;
}

const blankToNull = (text: string): string | null => {
  const trimmed = text.trim();
  return trimmed === "" ? null : trimmed;
};

/**
 * Randevu formu → `POST /wash/appointments` gövdesi: boş isteğe bağlı alanlar null (Zod'un `min(2)` hatası BOŞ alanda
 * çıkmasın), plaka boşluksuz büyük harf.
 */
export function appointmentBody(unitId: string, values: AppointmentFormValues): Record<string, unknown> {
  const plate = blankToNull(values.plate);
  return {
    unitId,
    startsAt: values.startsAt,
    programIds: values.programIds,
    plate: plate === null ? null : plate.replace(/[\s-]+/g, "").toUpperCase(),
    customerProfileId: values.customerProfileId,
    contactName: blankToNull(values.contactName),
    contactPhone: blankToNull(values.contactPhone),
    note: blankToNull(values.note),
  };
}

/** Program süreleri toplamı (dakikaya yukarı) — sunucunun `handWashDurationMinutes` kuralıyla aynı. */
export function handWashDurationMinutes(
  programs: readonly { durationSeconds: number | null }[],
): number | null {
  if (programs.length === 0 || programs.some((program) => program.durationSeconds === null)) return null;
  const total = programs.reduce((sum, program) => sum + (program.durationSeconds ?? 0), 0);
  return Math.ceil(total / 60);
}

// ── İş emri ────────────────────────────────────────────────────────────────────
export interface WorkOrderLineValues {
  programId: string;
  quantity: string;
}

export interface WorkOrderFormValues {
  unitId: string;
  plate: string;
  customerProfileId: string | null;
  contactName: string;
  contactPhone: string;
  note: string;
  staffId: string;
  lines: WorkOrderLineValues[];
}

/** Satırlar → gövde: programı seçilmemiş satır atılır, adet tam sayıya çevrilir (çevrilemezse metin kalır → Zod hatası). */
export function workOrderLinesBody(
  lines: readonly WorkOrderLineValues[],
): Array<{ programId: string; quantity: number | string }> {
  return lines
    .filter((line) => line.programId !== "")
    .map((line) => ({
      programId: line.programId,
      quantity: toInt(line.quantity === "" ? "1" : line.quantity),
    }));
}

/** İş emri formu → `POST /wash/work-orders` gövdesi (doğrulamadan önce normalleştirilmiş). */
export function createWorkOrderBody(stationId: string, values: WorkOrderFormValues): Record<string, unknown> {
  return {
    stationId,
    unitId: values.unitId === "" ? null : values.unitId,
    plate: values.plate.replace(/[\s-]+/g, "").toUpperCase(),
    customerProfileId: values.customerProfileId,
    contactName: blankToNull(values.contactName),
    contactPhone: blankToNull(values.contactPhone),
    note: blankToNull(values.note),
    staffId: values.staffId === "" ? null : values.staffId,
    lines: workOrderLinesBody(values.lines),
  };
}

/** Tahmini tutar: satır adedi × programın güncel fiyatı (sunucu kaydederken fiyatın anlık görüntüsünü alır). */
export function estimateWorkOrderTotal(
  lines: readonly WorkOrderLineValues[],
  unitPriceOf: (programId: string) => string | null,
): { total: Money; missingPrice: boolean } {
  let total = Money.ZERO;
  let missingPrice = false;
  for (const line of lines) {
    if (line.programId === "") continue;
    const price = unitPriceOf(line.programId);
    const quantity = /^\d+$/.test(line.quantity.trim()) ? line.quantity.trim() : "1";
    if (price === null) missingPrice = true;
    else total = total.add(Money.fromUnitPrice(price, quantity));
  }
  return { total, missingPrice };
}

/**
 * `GET /wash/programs?stationId` → program kimliği → güncel istasyon fiyatı (₺, ondalık metin; `estimateWorkOrderTotal`'ın
 * beklediği biçim). Fiyatı tanımsız program haritada yoktur.
 */
export function programPrices(
  programs: readonly Pick<WashProgram, "id" | "currentPrice">[],
): Map<string, string> {
  const prices = new Map<string, string>();
  for (const program of programs)
    if (program.currentPrice !== null)
      prices.set(program.id, Money.fromMinor(program.currentPrice).toMajorString());
  return prices;
}

/** Randevunun tahmini tutarı: seçilen her hizmet 1 adet × güncel fiyat (check-in iş emrinin kuralı). */
export function estimateServicesTotal(
  programIds: readonly string[],
  unitPriceOf: (programId: string) => string | null,
): { total: Money; missingPrice: boolean } {
  return estimateWorkOrderTotal(
    programIds.map((programId) => ({ programId, quantity: "1" })),
    unitPriceOf,
  );
}

/** Pano sütunları: bekleyen / işlemde / hazır + BUGÜN teslim edilenler (iptaller panoda yok). */
export const BOARD_COLUMNS = ["WAITING", "IN_PROGRESS", "READY", "DELIVERED"] as const;
export type BoardColumn = (typeof BOARD_COLUMNS)[number];

export function boardColumns(
  orders: readonly WashWorkOrderSummary[],
  today: LocalDate,
): Record<BoardColumn, WashWorkOrderSummary[]> {
  const columns: Record<BoardColumn, WashWorkOrderSummary[]> = {
    WAITING: [],
    IN_PROGRESS: [],
    READY: [],
    DELIVERED: [],
  };
  for (const order of orders) {
    if (order.status === "CANCELLED") continue;
    if (order.status === "DELIVERED") {
      if (order.deliveredAt && toIstanbulDate(new Date(order.deliveredAt)) === today)
        columns.DELIVERED.push(order);
      continue;
    }
    columns[order.status].push(order);
  }
  const since = (order: WashWorkOrderSummary) =>
    order.status === "IN_PROGRESS"
      ? order.inProgressAt
      : order.status === "READY"
        ? order.readyAt
        : order.status === "DELIVERED"
          ? order.deliveredAt
          : order.waitingAt;
  // Bekleyen / işlemde / hazır: en eski önce (geliş sırası); teslim edilenler: en yeni önce.
  for (const key of ["WAITING", "IN_PROGRESS", "READY"] as const)
    columns[key].sort((a, b) => (since(a) ?? "").localeCompare(since(b) ?? ""));
  columns.DELIVERED.sort((a, b) => (since(b) ?? "").localeCompare(since(a) ?? ""));
  return columns;
}

/** Durum makinesinin İLERİ adımı (büyük düğme): Başla → Hazır → Teslim et; iptal ayrı düğmedir. */
export function forwardTransition(
  status: WashWorkOrderStatus,
  allowed: readonly WashWorkOrderStatus[],
): Exclude<WashWorkOrderStatus, "WAITING" | "CANCELLED"> | null {
  const next =
    status === "WAITING"
      ? "IN_PROGRESS"
      : status === "IN_PROGRESS"
        ? "READY"
        : status === "READY"
          ? "DELIVERED"
          : null;
  return next !== null && allowed.includes(next) ? next : null;
}

/** Ödeme durumu (teslim penceresi ve ayrıntı): bağlı satış + uyarı; ödemesiz teslim gerekçesi; hiçbiri. */
export function paymentState(
  order: Pick<WashWorkOrderSummary, "posSaleId" | "paymentWarning">,
): "paid" | "warning" | "unpaid" {
  if (order.posSaleId === null) return "unpaid";
  return order.paymentWarning === null ? "paid" : "warning";
}

/** İş emri durumlarının hangisinde satırlar düzenlenebilir (sunucu kuralı). */
export function isWorkOrderEditable(status: WashWorkOrderStatus): boolean {
  return status === "WAITING" || status === "IN_PROGRESS";
}

// ── Sıra ekranı ────────────────────────────────────────────────────────────────
export const QUEUE_COLUMNS = ["WAITING", "IN_PROGRESS", "READY"] as const;
export type QueueColumn = (typeof QUEUE_COLUMNS)[number];

export function queueColumns<T extends { status: QueueColumn }>(
  items: readonly T[],
): Record<QueueColumn, T[]> {
  const columns: Record<QueueColumn, T[]> = { WAITING: [], IN_PROGRESS: [], READY: [] };
  // Sunucu sırası korunur (hazır en eski önce, işlemde, bekleyen geliş sırası).
  for (const item of items) columns[item.status].push(item);
  return columns;
}

// ── Raporlar ───────────────────────────────────────────────────────────────────
/** Varsayılan aralık: son 30 gün (bugün dâhil). */
export function defaultReportRange(today: LocalDate): { from: LocalDate; to: LocalDate } {
  const start = new Date(`${today}T12:00:00Z`);
  start.setUTCDate(start.getUTCDate() - 29);
  return { from: localDate(start.toISOString().slice(0, 10)), to: today };
}

const moneyNumber = (minor: string): number => Number(Money.fromMinor(minor).toMajorString());

export function marginTone(percent: string | null): Tone {
  if (percent === null) return "neutral";
  const value = Decimal.from(percent);
  return value.isNegative() ? "danger" : value.lt(20) ? "warning" : "success";
}

/**
 * Paylaştırılamayan su / elektrik (sunucunun `unallocated`'ı) → özet tablosunun ayrı satırı: yalnızca ilgili maliyet
 * sütunu, toplam maliyet ve (eksi) marj dolar — böylece satırlar + bu satırlar = toplam. Ünite / program satırına GİRMEZ.
 */
export function unallocatedSummaryRow(item: WashUnallocatedUtility, label: string): WashSummaryRow {
  const cost = Money.fromMinor(item.cost);
  const water = item.utility === "WATER";
  return {
    key: null,
    label,
    count: 0,
    posRevenue: "0",
    machineRevenue: "0",
    revenue: "0",
    chemicalCost: "0",
    waterCost: water ? item.cost : "0",
    electricityCost: water ? "0" : item.cost,
    totalCost: item.cost,
    margin: cost.neg().minor.toString(),
    marginPercent: null,
    waterM3: water ? item.quantity : "0.000",
    electricityKwh: water ? "0.000" : item.quantity,
  };
}

/** Özette gösterilecek bir şey var mı: satır ya da paylaştırılamayan tüketim. */
export function summaryHasData(summary: Pick<WashSummary, "rows" | "unallocated">): boolean {
  return summary.rows.length > 0 || summary.unallocated.length > 0;
}

export interface SummaryChartLabels {
  revenue: string;
  cost: string;
  margin: string;
}

/** Özet → yatay çubuk grafik (ciro / toplam maliyet / marj, ₺). Satır sırası tabloyla aynıdır (yukarıdan aşağı). */
export function summaryChartOption(rows: readonly WashSummaryRow[], labels: SummaryChartLabels): ChartOption {
  const ordered = [...rows].reverse();
  const series = [
    { name: labels.revenue, pick: (row: WashSummaryRow) => row.revenue },
    { name: labels.cost, pick: (row: WashSummaryRow) => row.totalCost },
    { name: labels.margin, pick: (row: WashSummaryRow) => row.margin },
  ];
  return {
    grid: GRID,
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      valueFormatter: (value) => `${formatChartNumber(value)} ₺`,
    },
    legend: { ...LEGEND_POSITION, data: series.map((item) => item.name) },
    xAxis: { type: "value", axisLabel: VALUE_AXIS_LABEL },
    yAxis: {
      type: "category",
      data: ordered.map((row) => row.label),
      axisLabel: { width: 140, overflow: "truncate" },
    },
    series: series.map((item) => ({
      type: "bar" as const,
      name: item.name,
      barMaxWidth: 18,
      data: ordered.map((row) => moneyNumber(item.pick(row))),
    })),
  };
}

export interface OccupancyRow {
  weekday: number;
  cells: Array<{ hour: number; seconds: number; percent: string }>;
  totalSeconds: number;
}

/** Doluluk matrisi → tablo satırları (hafta günü × saat). Eksik hücreler 0 kabul edilir. */
export function occupancyRows(occupancy: Pick<WashOccupancy, "seconds" | "percent">): OccupancyRow[] {
  return Array.from({ length: 7 }, (_unused, weekday) => {
    const cells = Array.from({ length: 24 }, (_empty, hour) => ({
      hour,
      seconds: occupancy.seconds[weekday]?.[hour] ?? 0,
      percent: occupancy.percent[weekday]?.[hour] ?? "0.0",
    }));
    return { weekday, cells, totalSeconds: cells.reduce((sum, cell) => sum + cell.seconds, 0) };
  });
}

/** Tabloda gösterilecek saat aralığı: çalışma olan ilk ve son saat (hiç yoksa 06–22). */
export function occupiedHourRange(rows: readonly OccupancyRow[]): { from: number; to: number } {
  let from = 24;
  let to = -1;
  for (const row of rows)
    for (const cell of row.cells)
      if (cell.seconds > 0) {
        from = Math.min(from, cell.hour);
        to = Math.max(to, cell.hour);
      }
  return to < 0 ? { from: 6, to: 22 } : { from, to };
}

export interface OccupancyChartLabels {
  weekdays: readonly string[];
  series: string;
}

/**
 * Doluluk → ECharts ısı haritası (x = saat, y = hafta günü, değer = doluluk %). `visualMap` sürekli ve 0–100 aralığında;
 * ipucu hücrenin günü, saati ve yüzdesini yazar.
 */
export function occupancyHeatmapOption(
  occupancy: Pick<WashOccupancy, "seconds" | "percent">,
  labels: OccupancyChartLabels,
): ChartOption {
  const rows = occupancyRows(occupancy);
  const data: Array<[number, number, number]> = [];
  let max = 0;
  for (const row of rows)
    for (const cell of row.cells) {
      const value = Number(cell.percent);
      max = Math.max(max, value);
      data.push([cell.hour, row.weekday, value]);
    }
  const hours = Array.from({ length: 24 }, (_unused, hour) => String(hour).padStart(2, "0"));
  return {
    grid: { left: 48, right: 24, top: 16, bottom: 72, containLabel: true },
    tooltip: {
      position: "top",
      formatter: (params: unknown) => {
        const value = (params as { value?: [number, number, number] }).value;
        if (!value) return "";
        return `${labels.weekdays[value[1]] ?? ""} ${hours[value[0]]}:00 — %${formatChartNumber(value[2])}`;
      },
    },
    xAxis: { type: "category", data: hours, splitArea: { show: true } },
    yAxis: { type: "category", data: [...labels.weekdays], inverse: true, splitArea: { show: true } },
    visualMap: {
      min: 0,
      max: Math.max(10, Math.ceil(max / 10) * 10),
      calculable: true,
      orient: "horizontal",
      left: "center",
      bottom: 0,
      // Tek renk + saydamlık: boş hücre zemine yakın kalır, açık ve koyu temada aynı okunur.
      inRange: { color: ["#0e7490"], colorAlpha: [0.06, 1] },
      formatter: (value: unknown) => `%${formatChartNumber(Number(value))}`,
    },
    series: [
      {
        type: "heatmap",
        name: labels.series,
        data,
        label: { show: false },
        emphasis: { itemStyle: { borderWidth: 1 } },
      },
    ],
  } as ChartOption;
}

/** Arıza süreleri → yığılmış yatay çubuk (ünite × arıza kodu, dakika). */
export function faultsChartOption(faults: Pick<WashFaults, "items">, minutesLabel: string): ChartOption {
  const units = [...faults.items].reverse();
  const codes = [
    ...new Set(faults.items.flatMap((item) => item.byCode.map((entry) => entry.faultCode))),
  ].sort();
  return {
    grid: GRID,
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      valueFormatter: (value) => `${formatChartNumber(value)} dk`,
    },
    legend: { ...LEGEND_POSITION, data: codes },
    xAxis: { type: "value", name: minutesLabel, axisLabel: VALUE_AXIS_LABEL },
    yAxis: {
      type: "category",
      data: units.map((item) => item.unitName),
      axisLabel: { width: 140, overflow: "truncate" },
    },
    series: codes.map((code) => ({
      type: "bar" as const,
      name: code,
      stack: "faults",
      barMaxWidth: 24,
      data: units.map(
        (item) =>
          Math.round(((item.byCode.find((entry) => entry.faultCode === code)?.seconds ?? 0) / 60) * 10) / 10,
      ),
    })),
  };
}

// ── Kredi ──────────────────────────────────────────────────────────────────────
/** Kredinin görünen adı: maskeli kart numarası; kartsız (kodlu) kredide sabit metin — kod ASLA gösterilmez. */
export function creditLabel(credit: { cardNoMasked: string | null }, codeLabel: string): string {
  return credit.cardNoMasked ?? codeLabel;
}

/** Defter satırının işaretli süresi: `+12 dk`, `−30 sn` (sıfır işaretsiz). */
export function signedDuration(seconds: string): string {
  const value = Number.parseInt(seconds, 10);
  if (!Number.isFinite(value) || value === 0) return formatDuration(0);
  return value > 0 ? `+${formatDuration(value)}` : formatDuration(value);
}

// ── Randevu saati ──────────────────────────────────────────────────────────────
/** İstanbul günü + "HH:MM" → ISO (UTC). Geçersiz girdide null. */
export function istanbulTimeToIso(date: string, time: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^([01]\d|2[0-3]):[0-5]\d$/.test(time)) return null;
  return istanbulDateTimeToUtc(localDate(date), time).toISOString();
}

/** Slot seçici etiketi: `09:30 · 2 boş` — dolu slot "dolu" yazar (anlam yalnızca renkle taşınmaz). */
export function slotText(
  slot: { startsAt: string; free: number; bookable: boolean },
  labels: { free: (count: number) => string; full: string },
): string {
  return `${formatClock(slot.startsAt)} · ${slot.bookable ? labels.free(slot.free) : labels.full}`;
}

/** Randevu "gelmedi" işaretlenebilir mi: geçiş izinli VE randevu saati gelmiş (sunucu kuralı). */
export function canMarkNoShow(
  appointment: { startsAt: string; allowedTransitions: readonly string[] },
  now: number,
): boolean {
  return (
    appointment.allowedTransitions.includes("NO_SHOW") && new Date(appointment.startsAt).getTime() <= now
  );
}
